import "./ClinicCard.css";
import parsePhoneNumber from "libphonenumber-js";
import React, { useContext } from "react";
import { getDistance } from "geolib";
import { GlobalContext } from "../../App";
import { CloseButton } from "../CloseButton";
import { motion } from "framer-motion/dist/framer-motion";
import { areCookiesEnabled } from "../../utils/areCookiesEnabled";

export const ClinicCard = React.forwardRef(({ clinic }, ref) => {
  const {
    userGPSCoordinates,
    visibleClinicCardIDs,
    setVisibleClinicCardIDs,
    setCenter,
    zoom,
    setZoom,
    searchValueCoordinates,
    value,
  } = useContext(GlobalContext);

  const { title } = clinic;

  const {
    streetAddress,
    streetNumber,
    streetName,
    postCode,
    longitude,
    latitude,
  } = clinic?.acfData?.address;

  const { overrideStreetAddress, telephone, website } = clinic?.acfData;

  const { buttons } = clinic?.acfData;

  const findFirstValidObject = (objects) => {
    // Use the find() method to search for the first object where mediaItemUrl is not null or undefined
    const foundObject = objects.find((obj) => obj.mediaItemUrl != null);

    return foundObject;
  };

  let featuredImageSourceUrl = "";
  let logoSourceUrl = "";

  if (clinic?.acfData?.image) {
    featuredImageSourceUrl = findFirstValidObject(
      clinic?.acfData?.image
    ).mediaItemUrl;
  }

  if (clinic?.acfData?.logo) {
    logoSourceUrl = findFirstValidObject(clinic?.acfData?.logo).mediaItemUrl;
  }

  const firstLine = `${streetNumber ? streetNumber : ""} ${streetName ?? ""}`;
  const fullAddress = `${
    firstLine !== " " ? `${firstLine},` : ""
  } ${streetAddress.split(",").splice(-2, 2).join(",").trim()}, ${postCode}`;

  let phoneNumber;

  if (telephone) {
    phoneNumber = parsePhoneNumber(telephone, "GB");
  }

  let distance;

  if (searchValueCoordinates) {
    distance = getDistance(
      {
        latitude: searchValueCoordinates.lat,
        longitude: searchValueCoordinates.lng,
      },
      {
        latitude,
        longitude,
      }
    );
  } else {
    if (userGPSCoordinates) {
      distance = getDistance(
        {
          latitude: userGPSCoordinates.lat,
          longitude: userGPSCoordinates.lng,
        },
        {
          latitude,
          longitude,
        }
      );
    }
  }

  const handleCloseButtonClick = () => {
    setVisibleClinicCardIDs(
      visibleClinicCardIDs.filter((cardID) => cardID !== clinic.id)
    );
  };

  const button = ({ title, link, isPrimary }) => (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      key={title}
      className={`clinic-card-button ${isPrimary ? "primary" : "secondary"}`}
    >
      {title}
    </a>
  );

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // drag
      // dragElastic={1}
      // dragConstraints={{
      //   left: 0,
      //   right: 0,
      //   top: 0,
      //   bottom: 0,
      // }}
      layout
      key={clinic.id}
      className="clinic-card-wrapper"
      onClick={(e) => {
        if (["svg", "path", "a"].includes(e.target.nodeName)) {
          e.stopPropagation();
        } else {
          setCenter({
            lat: latitude,
            lng: longitude,
          });

          if (zoom < 11) {
            setZoom(11);
          }

          if (areCookiesEnabled() && window.ga) {
            window.ga("send", "event", "Map", "contact", clinic.title);
          }
        }
      }}
    >
      <div className="clinic-card-title-bar-wrapper">
        {/* only show the logo if the url exists */}
        {logoSourceUrl && (
          <img src={logoSourceUrl} alt={` Logo`} width="30" height="30" />
        )}
        <h4>
          {website ? (
            <a
              target="_blank"
              className="clinic-title-link"
              rel="noreferrer"
              href={website}
            >
              {title}
            </a>
          ) : (
            title
          )}
        </h4>
        <CloseButton onClick={handleCloseButtonClick} />
      </div>
      {/* only show the featured image if the url exists */}
      {featuredImageSourceUrl && (
        <img
          src={featuredImageSourceUrl}
          alt={title}
          className="featured-image"
        />
      )}
      <div className="clinic-card-bottom-details-wrapper">
        <section>
          <a
            href={`https://www.google.com/maps/search/${
              overrideStreetAddress || fullAddress
            }`}
            target="_blank"
            rel="noreferrer"
            className="font-14 font-bold"
          >
            {overrideStreetAddress || fullAddress}
          </a>
        </section>
        {phoneNumber && (
          <section>
            <a className="font-14 font-normal" href={phoneNumber.getURI()}>
              {phoneNumber.formatNational()}
            </a>
          </section>
        )}
        {/* only show GPS distance if we have the user's GPS coordinates */}
        {distance && (
          <section>
            <p className="font-14">
              {parseFloat(distance / 1000).toFixed(1)} km away from{" "}
              {value?.structured_formatting?.main_text ||
                "your current location"}
            </p>
          </section>
        )}
        <section>
          <div className="clinic-card-buttons space-x-2">
            {buttons && buttons.length
              ? buttons.map(({ title, link }, index) =>
                  button({ title, link, isPrimary: index === 0 })
                )
              : button({
                  title: "Visit Website",
                  link: website,
                  primary: true,
                })}
          </div>
        </section>
      </div>
    </motion.div>
  );
});
